import type { ClaimFlowStepView } from '../claims/claimFlowModel'

export interface ClaimsProgressRailProps {
  steps: ClaimFlowStepView[]
}

function StepGlyph() {
  return (
    <span
      className="flex size-[28px] shrink-0 items-center justify-center rounded-full bg-foreground-brand-primary-alt text-text-primary-900"
      aria-hidden
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path
          d="M11.5 3.5L5.25 9.75L2.5 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/**
 * Desktop-only claim history rail (completed steps only; paired with {@link deriveClaimFlowSteps}).
 */
export default function ClaimsProgressRail({ steps }: ClaimsProgressRailProps) {
  const historySteps = steps.filter((step) => step.status === 'complete')
  if (historySteps.length === 0) {
    return null
  }

  return (
    <nav
      className="rounded-xl border border-solid border-border-primary bg-background-primary p-[20px] shadow-xs"
      aria-label="Claim progress"
    >
      <p className="m-0 mb-[16px] font-display text-md font-semibold leading-md text-text-primary-900">Your claim</p>
      <ol className="m-0 flex list-none flex-col gap-0 p-0">
        {historySteps.map((step, index) => {
          const isLast = index === historySteps.length - 1
          return (
            <li key={step.id} className="relative flex gap-[12px]">
              <div className="flex flex-col items-center">
                <StepGlyph />
                {!isLast ? (
                  <span
                    aria-hidden
                    className="my-[4px] block min-h-[24px] w-[2px] shrink-0 rounded-full bg-foreground-brand-primary-alt/55"
                  />
                ) : null}
              </div>
              <div className={`min-w-0 flex-1 ${isLast ? '' : 'pb-[16px]'}`}>
                <p className="m-0 font-body text-sm font-semibold leading-sm text-text-primary-900">
                  {step.title}
                </p>
                {step.summary ? (
                  <p className="mt-[4px] m-0 font-body text-xs leading-xs text-text-tertiary-600">{step.summary}</p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
